import { CognitoIdentityProviderClient, InitiateAuthCommand, GetUserCommand, RespondToAuthChallengeCommand, SignUpCommand, ConfirmSignUpCommand } from '@aws-sdk/client-cognito-identity-provider';
import { apiService } from './apiService';

// AWS Cognito 인증 서비스
class AuthService {
  constructor() {
    this.isAuthenticated = false;
    this.user = null;
    this.token = null;
    this.accessToken = null;
    this.refreshToken = null;
    
    // AWS Cognito 설정
    this.region = process.env.REACT_APP_AWS_REGION || 'ap-northeast-2';
    this.userPoolId = process.env.REACT_APP_USER_POOL_ID;
    this.clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    this.identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID;
    
    // Cognito 클라이언트 초기화
    this.cognitoClient = new CognitoIdentityProviderClient({
      region: this.region
    });

    
    console.log('Cognito 설정:', {
      region: this.region,
      userPoolId: this.userPoolId,
      clientId: this.clientId,
      identityPoolId: this.identityPoolId
    });
  }

  // 토큰 만료 여부 확인
  isTokenExpired(token) {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      // 30초 여유를 두고 만료 체크 (네트워크 지연 고려)
      return payload.exp < (currentTime + 30);
    } catch (error) {
      console.error('토큰 만료 체크 오류:', error);
      return true;
    }
  }

  // Refresh Token을 사용하여 토큰 갱신 (백엔드 API 사용)
  async refreshTokens() {
    console.log('토큰 갱신 시도 중...');
    
    if (!this.refreshToken) {
      console.error('Refresh token이 없습니다');
      throw new Error('Refresh token이 없습니다');
    }

    try {
      // 백엔드 토큰 갱신 API 호출
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://your-api-gateway-url.amazonaws.com/dev';
      
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refreshToken: this.refreshToken
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // 새 토큰들 저장
        if (data.AuthenticationResult.AccessToken) {
          this.accessToken = data.AuthenticationResult.AccessToken;
          localStorage.setItem('accessToken', this.accessToken);
        }
        
        if (data.AuthenticationResult.IdToken) {
          this.token = data.AuthenticationResult.IdToken;
          localStorage.setItem('token', this.token);
        }
        
        console.log('토큰 갱신 성공 (백엔드 API)');
        return true;
        
      } else {
        const errorData = await response.json();
        console.error('토큰 갱신 API 오류:', errorData);
        
        // Refresh Token 만료 시 로그아웃 처리
        if (response.status === 401) {
          console.log('Refresh token 만료, 로그아웃 처리');
          await this.signOut();
        }
        
        throw new Error(errorData.message || '토큰 갱신 실패');
      }
      
    } catch (error) {
      console.error('토큰 갱신 실패:', error);
      
      // 네트워크 오류 등으로 백엔드 API 실패 시 Cognito 직접 호출 (fallback)
      try {
        console.log('Cognito 직접 호출로 fallback...');
        
        const command = new InitiateAuthCommand({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: this.clientId,
          AuthParameters: {
            REFRESH_TOKEN: this.refreshToken
          }
        });

        const cognitoResponse = await this.cognitoClient.send(command);
        
        if (cognitoResponse.AuthenticationResult) {
          // 새 토큰들 저장
          if (cognitoResponse.AuthenticationResult.AccessToken) {
            this.accessToken = cognitoResponse.AuthenticationResult.AccessToken;
            localStorage.setItem('accessToken', this.accessToken);
          }
          
          if (cognitoResponse.AuthenticationResult.IdToken) {
            this.token = cognitoResponse.AuthenticationResult.IdToken;
            localStorage.setItem('token', this.token);
          }
          
          console.log('토큰 갱신 성공 (Cognito 직접)');
          return true;
        }
        
      } catch (cognitoError) {
        console.error('Cognito 직접 호출도 실패:', cognitoError);
        
        // Refresh Token이 만료되었거나 유효하지 않은 경우
        if (cognitoError.name === 'NotAuthorizedException' || cognitoError.name === 'UserNotConfirmedException') {
          console.log('Refresh token 완전 만료, 로그아웃 처리');
          await this.signOut();
        }
        
        throw cognitoError;
      }
    }
    
    return false;
  }

  // 유효한 토큰 확보 (만료 시 자동 갱신)
  async ensureValidToken() {
    if (this.isTokenExpired(this.token)) {
      console.log('토큰 만료됨, 갱신 시도...');
      
      try {
        await this.refreshTokens();
      } catch (error) {
        console.error('토큰 갱신 실패:', error);
        throw new Error('토큰 갱신 실패: 다시 로그인해주세요');
      }
    }
  }

  // 로그인 상태 확인
  async checkAuthStatus() {
    try {
      // 로컬 스토리지에서 토큰 확인
      const storedAccessToken = localStorage.getItem('auth_access_token');
      const storedIdToken = localStorage.getItem('auth_id_token');
      const storedRefreshToken = localStorage.getItem('auth_refresh_token');
      const storedUser = localStorage.getItem('auth_user');
      
      if (storedAccessToken && storedIdToken && storedUser) {
        this.accessToken = storedAccessToken;
        this.token = storedIdToken;
        this.refreshToken = storedRefreshToken;
        this.user = JSON.parse(storedUser);
        
        // 토큰 유효성 검사 및 자동 갱신
        try {
          // 토큰 만료 시 자동 갱신 시도
          if (this.isTokenExpired(this.token)) {
            console.log('저장된 토큰이 만료됨, 갱신 시도...');
            await this.refreshTokens();
          }

          const tokenPayload = JSON.parse(atob(this.token.split('.')[1]));
          
          // 토큰에서 그룹 정보 추출
          const groups = tokenPayload['cognito:groups'] || [];
          this.isAdmin = groups.includes('Admin');
          
          if (!this.isTokenExpired(this.token)) {
            // 토큰이 유효하지만 사용자 상태도 확인
            const statusCheck = await this.checkUserStatus(this.user.email);
            if (!statusCheck.allowed) {
              // 상태가 변경된 경우 로그아웃 처리
              await this.signOut();
              return {
                isAuthenticated: false,
                user: null,
                token: null,
                statusMessage: statusCheck.message
              };
            }
            
            this.isAuthenticated = true;
            return {
              isAuthenticated: true,
              user: { ...this.user, isAdmin: this.isAdmin },
              token: this.token,
              isAdmin: this.isAdmin
            };
          } else {
            // 토큰이 만료된 경우 리프레시 시도
            if (this.refreshToken) {
              await this.refreshAuthToken();
              return {
                isAuthenticated: true,
                user: this.user,
                token: this.token
              };
            }
          }
        } catch (jwtError) {
          console.error('JWT 파싱 오류:', jwtError);
        }
      }
      
      // 인증 상태 초기화
      await this.signOut();
      return {
        isAuthenticated: false,
        user: null,
        token: null
      };
    } catch (error) {
      console.error('인증 상태 확인 오류:', error);
      await this.signOut();
      return {
        isAuthenticated: false,
        user: null,
        token: null
      };
    }
  }

  // 사용자 상태 확인
  async checkUserStatus(email) {
    try {
      const result = await apiService.post('/user-status-check', { email });
      
      return {
        success: true,
        allowed: result.allowed,
        reason: result.reason,
        message: result.message,
        userInfo: result.user_info
      };
    } catch (error) {
      console.error('사용자 상태 확인 오류:', error);
      return {
        success: false,
        allowed: false,
        reason: 'NETWORK_ERROR',
        message: '네트워크 오류가 발생했습니다.'
      };
    }
  }

  // 로그인
  async signIn(credentials) {
    try {
      if (!this.userPoolId || !this.clientId) {
        throw new Error('Cognito 설정이 누락되었습니다. 환경 변수를 확인하세요.');
      }

      // 로그인 전 사용자 상태 확인
      const statusCheck = await this.checkUserStatus(credentials.username);
      if (!statusCheck.allowed) {
        return {
          success: false,
          error: statusCheck.message || '계정 접근이 제한되었습니다.'
        };
      }

      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          USERNAME: credentials.username,
          PASSWORD: credentials.password
        }
      });

      const response = await this.cognitoClient.send(command);
      
      // NEW_PASSWORD_REQUIRED 챌린지 처리
      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: response.Session,
          message: '첫 로그인입니다. 새 비밀번호를 설정해주세요.',
          userAttributes: response.ChallengeParameters
        };
      }
      
      if (response.AuthenticationResult) {
        this.accessToken = response.AuthenticationResult.AccessToken;
        this.token = response.AuthenticationResult.IdToken;
        this.refreshToken = response.AuthenticationResult.RefreshToken;
        
        // 사용자 정보 가져오기
        const userInfo = await this.getUserInfo();
        this.user = userInfo;
        this.isAuthenticated = true;
        
        // 로컬 스토리지에 저장
        localStorage.setItem('auth_access_token', this.accessToken);
        localStorage.setItem('auth_id_token', this.token);
        localStorage.setItem('auth_refresh_token', this.refreshToken);
        localStorage.setItem('auth_user', JSON.stringify(userInfo));
        
        return {
          success: true,
          user: userInfo,
          token: this.token,
          isAdmin: userInfo.isAdmin
        };
      } else {
        throw new Error('로그인에 실패했습니다.');
      }
    } catch (error) {
      console.error('Cognito 로그인 오류:', error);
      
      // Cognito 오류 메시지 사용자 친화적으로 변환
      let errorMessage = '로그인에 실패했습니다.';
      
      if (error.name === 'NotAuthorizedException') {
        errorMessage = '사용자명 또는 비밀번호가 잘못되었습니다.';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = '계정 확인이 필요합니다. 이메일을 확인하세요.';
      } else if (error.name === 'PasswordResetRequiredException') {
        errorMessage = '비밀번호 재설정이 필요합니다.';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = '존재하지 않는 사용자입니다.';
      } else if (error.name === 'TooManyRequestsException') {
        errorMessage = '너무 많은 요청이 있었습니다. 잠시 후 다시 시도하세요.';
      }
      
      throw new Error(errorMessage);
    }
  }
  
  // 사용자 정보 가져오기
  async getUserInfo() {
    try {
      const command = new GetUserCommand({
        AccessToken: this.accessToken
      });
      
      const response = await this.cognitoClient.send(command);
      
      // JWT 토큰에서 그룹 정보 추출
      let groups = [];
      let isAdmin = false;
      
      if (this.token) {
        try {
          const tokenPayload = JSON.parse(atob(this.token.split('.')[1]));
          groups = tokenPayload['cognito:groups'] || [];
          isAdmin = groups.includes('Admin');
        } catch (error) {
          console.error('JWT 토큰 파싱 오류:', error);
        }
      }
      
      // 사용자 정보 가공
      const userInfo = {
        username: response.Username,
        sub: response.Username,
        email: '',
        name: '',
        organization: '',
        groups: groups,
        isAdmin: isAdmin
      };
      
      // UserAttributes에서 정보 추출
      if (response.UserAttributes) {
        response.UserAttributes.forEach(attr => {
          switch (attr.Name) {
            case 'email':
              userInfo.email = attr.Value;
              break;
            case 'name':
              userInfo.name = attr.Value;
              break;
            case 'custom:organization':
              userInfo.organization = attr.Value;
              break;
            case 'given_name':
              userInfo.givenName = attr.Value;
              break;
            case 'family_name':
              userInfo.familyName = attr.Value;
              break;
            default:
              // 다른 속성들은 무시
              break;
          }
        });
      }
      
      return userInfo;
    } catch (error) {
      console.error('사용자 정보 가져오기 오류:', error);
      throw error;
    }
  }

  // 로그아웃
  async signOut() {
    try {
      // 인증 상태 초기화
      this.user = null;
      this.token = null;
      this.accessToken = null;
      this.refreshToken = null;
      this.isAuthenticated = false;
      
      // 로컬 스토리지 정리
      localStorage.removeItem('auth_access_token');
      localStorage.removeItem('auth_id_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_user');
      
      return {
        success: true
      };
    } catch (error) {
      console.error('로그아웃 오류:', error);
      throw error;
    }
  }

  // 새 비밀번호 설정 (NEW_PASSWORD_REQUIRED 챌린지 응답)
  async setNewPassword(session, newPassword, username) {
    try {
      if (!session || !newPassword) {
        throw new Error('세션 정보와 새 비밀번호가 필요합니다.');
      }

      const command = new RespondToAuthChallengeCommand({
        ClientId: this.clientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword
        }
      });

      const response = await this.cognitoClient.send(command);

      if (response.AuthenticationResult) {
        this.accessToken = response.AuthenticationResult.AccessToken;
        this.token = response.AuthenticationResult.IdToken;
        this.refreshToken = response.AuthenticationResult.RefreshToken;

        // 사용자 정보 가져오기
        const userInfo = await this.getUserInfo();
        this.user = userInfo;
        this.isAuthenticated = true;

        // 로컬 스토리지에 저장
        localStorage.setItem('auth_access_token', this.accessToken);
        localStorage.setItem('auth_id_token', this.token);
        localStorage.setItem('auth_refresh_token', this.refreshToken);
        localStorage.setItem('auth_user', JSON.stringify(userInfo));

        return {
          success: true,
          user: userInfo,
          token: this.token
        };
      } else {
        throw new Error('새 비밀번호 설정에 실패했습니다.');
      }
    } catch (error) {
      console.error('새 비밀번호 설정 오류:', error);
      
      let errorMessage = '새 비밀번호 설정에 실패했습니다.';
      
      if (error.name === 'InvalidPasswordException') {
        errorMessage = '비밀번호가 정책에 맞지 않습니다. 8자 이상, 대소문자, 숫자, 특수문자를 포함해야 합니다.';
      } else if (error.name === 'InvalidParameterException') {
        errorMessage = '잘못된 매개변수입니다. 비밀번호를 다시 확인해주세요.';
      }
      
      throw new Error(errorMessage);
    }
  }

  // 토큰 갱신
  async refreshAuthToken() {
    try {
      if (!this.refreshToken) {
        throw new Error('리프레시 토큰이 없습니다.');
      }
      
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          REFRESH_TOKEN: this.refreshToken
        }
      });
      
      const response = await this.cognitoClient.send(command);
      
      if (response.AuthenticationResult) {
        this.accessToken = response.AuthenticationResult.AccessToken;
        this.token = response.AuthenticationResult.IdToken;
        
        // 로컬 스토리지 업데이트
        localStorage.setItem('auth_access_token', this.accessToken);
        localStorage.setItem('auth_id_token', this.token);
        
        return {
          success: true,
          token: this.token
        };
      } else {
        throw new Error('토큰 갱신에 실패했습니다.');
      }
    } catch (error) {
      console.error('토큰 갱신 오류:', error);
      // 갱신 실패 시 로그아웃
      await this.signOut();
      throw error;
    }
  }

  // 현재 사용자 정보
  getCurrentUser() {
    return this.user;
  }

  // 현재 토큰 정보
  getCurrentToken() {
    return this.token;
  }

  // 회원가입
  async signUp({ email, password, name, organization }) {
    try {
      if (!this.clientId) {
        throw new Error('Cognito 설정이 누락되었습니다. 환경 변수를 확인하세요.');
      }

      const userAttributes = [
        {
          Name: 'email',
          Value: email
        }
      ];

      // name이 제공된 경우 추가
      if (name && name.trim()) {
        userAttributes.push({
          Name: 'name',
          Value: name.trim()
        });
      }

      // organization이 제공된 경우 추가
      if (organization && organization.trim()) {
        userAttributes.push({
          Name: 'custom:organization',
          Value: organization.trim()
        });
      }

      const command = new SignUpCommand({
        ClientId: this.clientId,
        Username: email,
        Password: password,
        UserAttributes: userAttributes
      });

      const response = await this.cognitoClient.send(command);
      
      return {
        success: true,
        userSub: response.UserSub,
        message: '회원가입이 완료되었습니다. 이메일을 확인하여 계정을 인증해주세요.'
      };
    } catch (error) {
      console.error('Cognito 회원가입 오류:', error);
      
      let errorMessage = '회원가입에 실패했습니다.';
      
      if (error.name === 'UsernameExistsException') {
        errorMessage = '이미 존재하는 이메일입니다.';
      } else if (error.name === 'InvalidPasswordException') {
        errorMessage = '비밀번호가 정책에 맞지 않습니다. (대소문자, 숫자 포함 8자 이상)';
      } else if (error.name === 'InvalidParameterException') {
        errorMessage = '입력된 정보가 올바르지 않습니다.';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // 이메일 인증 확인
  async confirmSignUp(email, confirmationCode) {
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: this.clientId,
        Username: email,
        ConfirmationCode: confirmationCode
      });

      await this.cognitoClient.send(command);
      
      return {
        success: true,
        message: '이메일 인증이 완료되었습니다. 로그인해주세요.'
      };
    } catch (error) {
      console.error('이메일 인증 오류:', error);
      
      let errorMessage = '이메일 인증에 실패했습니다.';
      
      if (error.name === 'CodeMismatchException') {
        errorMessage = '인증 코드가 일치하지 않습니다.';
      } else if (error.name === 'ExpiredCodeException') {
        errorMessage = '인증 코드가 만료되었습니다.';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // 관리자 권한 확인
  isUserAdmin() {
    return this.isAuthenticated && this.user && this.user.isAdmin === true;
  }

  // 사용자 그룹 확인
  getUserGroups() {
    if (this.isAuthenticated && this.user && this.user.groups) {
      return this.user.groups;
    }
    return [];
  }

  // 특정 그룹 멤버십 확인
  hasGroup(groupName) {
    const groups = this.getUserGroups();
    return groups.includes(groupName);
  }

  // 현재 사용자의 권한 정보 반환
  getAuthInfo() {
    return {
      isAuthenticated: this.isAuthenticated,
      user: this.user,
      token: this.token,
      isAdmin: this.isUserAdmin(),
      groups: this.getUserGroups()
    };
  }

  // ID 토큰 반환 (API 호출용)
  async getIdToken() {
    try {
      await this.ensureValidToken();
      return this.token;
    } catch (error) {
      console.error('ID 토큰 획득 실패:', error);
      return null;
    }
  }

  // Access 토큰 반환
  async getAccessToken() {
    try {
      await this.ensureValidToken();
      return this.accessToken;
    } catch (error) {
      console.error('Access 토큰 획득 실패:', error);
      return null;
    }
  }

  // 토큰 만료까지 남은 시간 (초)
  getTokenTimeLeft() {
    if (!this.token) return 0;
    
    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return Math.max(0, payload.exp - currentTime);
    } catch (error) {
      return 0;
    }
  }
}

// AuthService 인스턴스 생성 및 내보내기
export const authService = new AuthService();

export default authService;